import type { PhrasesTreeInput } from '../progression.js';

/**
 * SAMPLE phrases course — the real one (content/courses/ka-en-phrases) is not
 * written yet. Used by the unit tests and by the /phrases-review design route
 * ONLY while the real course is missing, so the album can be judged on a
 * preview link with the real level sizes (10/15/20/20/20/15 lessons, a
 * review every 5th). Phrases come from PHRASES.md § 8; when a level's list
 * runs out it repeats — this is layout data, not the curriculum.
 */

interface SamplePhrase {
  en: string;
  ka: string;
}

const LEVELS: Array<{ code: string; title: string; lessons: number; phrases: SamplePhrase[] }> = [
  {
    code: 'a1',
    title: 'საწყისი',
    lessons: 10,
    phrases: [
      { en: 'Hold on!', ka: 'მოიცა!' },
      { en: 'Never mind.', ka: 'არაუშავს' },
      { en: 'Sorry?', ka: 'ვერ გავიგე' },
      { en: 'Sounds good!', ka: 'კარგი აზრია' },
      { en: 'Watch out!', ka: 'ფრთხილად!' },
      { en: 'Bless you!', ka: 'იყავი ჯანმრთელად' },
      { en: 'Good luck!', ka: 'წარმატებები' },
      { en: 'Me neither.', ka: 'არც მე' },
    ],
  },
  {
    code: 'a2',
    title: 'ელემენტარული',
    lessons: 15,
    phrases: [
      { en: 'No way!', ka: 'არ შეიძლება!' },
      { en: "I'm starving.", ka: 'ძალიან მშია' },
      { en: "It's up to you.", ka: 'შენ გადაწყვიტე' },
      { en: 'Same here.', ka: 'მეც ასე' },
      { en: "What's up?", ka: 'რა ხდება?' },
      { en: "I'm on my way.", ka: 'გზაში ვარ' },
      { en: 'Give me a hand.', ka: 'მომეხმარე' },
      { en: "I can't wait!", ka: 'მოუთმენლად ველი!' },
      { en: 'Take your time.', ka: 'ნუ იჩქარებ' },
      { en: 'Make yourself at home.', ka: 'თავი შინ იგრძენი' },
      { en: "It doesn't matter.", ka: 'მნიშვნელობა არ აქვს' },
      { en: 'Just a moment.', ka: 'ერთი წუთით' },
    ],
  },
  {
    code: 'b1',
    title: 'საშუალო',
    lessons: 20,
    phrases: [
      { en: 'Fair enough.', ka: 'გასაგებია' },
      { en: 'I could use a break.', ka: 'დასვენება მჭირდება' },
      { en: 'Piece of cake!', ka: 'ძალიან ადვილია' },
      { en: 'By the way', ka: 'სხვათა შორის' },
      { en: 'To be honest', ka: 'მართალი გითხრა' },
      { en: 'Give up', ka: 'დანებება' },
      { en: 'Figure out', ka: 'მიხვედრა' },
      { en: 'Break the ice', ka: 'უხერხულობის მოხსნა' },
    ],
  },
  {
    code: 'b2',
    title: 'საშუალოზე მაღალი',
    lessons: 20,
    phrases: [
      { en: 'Keep me posted.', ka: 'საქმის კურსში მამყოფე' },
      { en: 'On the same page', ka: 'ერთნაირად ვფიქრობთ' },
      { en: 'Get the hang of it', ka: 'ალღოს აღება' },
      { en: 'The ball is in your court.', ka: 'შენი სვლაა' },
      { en: 'Hit the nail on the head', ka: 'ზუსტად მიხვედრა' },
      { en: 'On the other hand', ka: 'მეორე მხრივ' },
      { en: 'It goes without saying', ka: 'თავისთავად ცხადია' },
      { en: "As far as I'm concerned", ka: 'რაც შემეხება მე' },
    ],
  },
  {
    code: 'c1',
    title: 'მაღალი',
    lessons: 20,
    phrases: [
      { en: 'By and large', ka: 'საერთო ჯამში' },
      { en: 'In the long run', ka: 'დროთა განმავლობაში' },
      { en: 'A blessing in disguise', ka: 'ცუდმა კარგი მოიტანა' },
      { en: 'The elephant in the room', ka: 'თემა, რომელსაც ყველა გაურბის' },
      { en: 'Take it for granted', ka: 'თავისთავად ცხადად მიჩნევა' },
      { en: 'To a large extent', ka: 'დიდწილად' },
      { en: 'Beat around the bush', ka: 'მიკიბ-მოკიბვა' },
      { en: 'Bite the bullet', ka: 'კბილის დაჭერა' },
    ],
  },
  {
    code: 'c2',
    title: 'სრულყოფილი',
    lessons: 15,
    phrases: [
      { en: 'By the same token', ka: 'ამავე ლოგიკით' },
      { en: 'To all intents and purposes', ka: 'ფაქტობრივად' },
      { en: 'A far cry from', ka: 'სულ სხვა რამ' },
      { en: 'The be-all and end-all', ka: 'ყველაზე მთავარი' },
      { en: 'A double-edged sword', ka: 'ორლესული მახვილი' },
      { en: 'A Pyrrhic victory', ka: 'პიროსის გამარჯვება' },
      { en: 'The early bird…', ka: 'ვინც ადრე დგება…' },
      { en: 'Lo and behold', ka: 'და აჰა' },
    ],
  },
];

const TEACHING = [
  ...Array.from({ length: 6 }, () => ({ type: 'phrase_intro' })),
  ...Array.from({ length: 14 }, () => ({ type: 'phrase_choice' })),
];
const REVIEW = Array.from({ length: 18 }, () => ({ type: 'phrase_choice' }));

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-|-$/gu, '');
}

/** Lesson ids follow the course's reserved blocks: A1 1101+, A2 1201+ … C2 1601+. */
export const SAMPLE_PHRASES_TREE: PhrasesTreeInput = {
  units: LEVELS.map((level, li) => {
    const lessons = Array.from({ length: level.lessons }, (_, i) => {
      const n = i + 1;
      const id = `lesson-${1100 + li * 100 + n}`;
      if (n % 5 === 0) {
        return { id: `${id}-review-${n / 5}`, title: 'გამეორება', exercises: REVIEW };
      }
      const teachingIndex = i - Math.floor(i / 5);
      const p = level.phrases[teachingIndex % level.phrases.length] ?? { en: 'Hold on!', ka: 'მოიცა!' };
      return { id: `${id}-${slug(p.en)}`, title: p.ka, tile_en: p.en, exercises: TEACHING };
    });
    const skills = [];
    for (let i = 0; i < lessons.length; i += 5) skills.push({ resolvedLessons: lessons.slice(i, i + 5) });
    return { id: `phr-${level.code}`, title: level.title, resolvedSkills: skills };
  }),
};
